import React from 'react';
import { useTranslation } from 'react-i18next';

import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';

import { OralExamErrorState } from './OralExamErrorState';

const UNSET_SUPPORT_EMAIL = '(no support email set)';

export type SessionVoidTechnicalScreenProps = {
  onLeave: () => void;
  leaveLabel?: string;
};

/**
 * SessionVoidTechnicalScreen
 * ----
 * Terminal screen when integrity void (`session_void`) cancels the booking.
 * Distinct from `examEndedConnection` used for `agent_unavailable`.
 */
export function SessionVoidTechnicalScreen({
  onLeave,
  leaveLabel,
}: SessionVoidTechnicalScreenProps) {
  const { t } = useTranslation();
  const rawSupportEmail = useTenantSettingsStore((s) => s.settings?.contact?.supportEmail);
  const supportEmail = rawSupportEmail && rawSupportEmail !== UNSET_SUPPORT_EMAIL
    ? rawSupportEmail
    : undefined;

  const title = t('oralExam.student.sessionVoidTechnical.title', {
    defaultValue: 'This session is no longer valid',
  });
  const message = supportEmail
    ? t('oralExam.student.sessionVoidTechnical.bodyWithSupport', {
      defaultValue:
          'Unfortunately this session is invalid due to technical difficulties. Your session is cancelled. Please register for another session. If problems persist, please reach out to {{supportEmail}}.',
      supportEmail,
    })
    : t('oralExam.student.sessionVoidTechnical.body', {
      defaultValue:
          'Unfortunately this session is invalid due to technical difficulties. Your session is cancelled. Please register for another session.',
    });

  return (
    <OralExamErrorState
      testID="oral-exam-session-void-technical"
      title={title}
      message={message}
      tone="warning"
      leave={onLeave}
      leaveLabel={leaveLabel}
    />
  );
}
